import { CopyIcon, DeleteIcon, PasteIcon, PlusIcon, RotateIcon, WarningIcon } from "@/components/icons";
import { AnchorIcon } from "@/components/icons/anchor-icon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useState } from "react";
import { toast } from "sonner";
import { PricingAnchor } from "../../pricing-configurator.types";
import { AI_PROMPT } from "../../pricing-configurator.const";

type PasteRow = {
  id: string;
  horaire: string;
  prix: string;
};

const TOKEN_PATTERN = /\d+\s?min|\d+(?:[.,]\d+)?\s?€/gi;

// Normalizes duration spacing (e.g. "15min" -> "15 min"). Returns the original text unchanged
const normalizeHoraireToMinutes = (token: string): string => {
  const minMatch = token.match(/^(\d+)\s?min$/i);
  return minMatch ? `${minMatch[1]} min` : token;
};

const extractPairsFromLine = (line: string): PasteRow[] => {
  const tokens = line.match(TOKEN_PATTERN)?.map((token) => token.trim()) ?? [];
  const rows: PasteRow[] = [];
  for (let i = 0; i + 2 <= tokens.length; i += 2) {
    rows.push({ id: crypto.randomUUID(), horaire: normalizeHoraireToMinutes(tokens[i]), prix: tokens[i + 1] });
  }
  return rows;
};

const parsePastedTable = (text: string): PasteRow[] => {
  const lines = text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const rows: PasteRow[] = [];
  lines.forEach((line) => {
    rows.push(...extractPairsFromLine(line));
  });

  return rows;
};

const parseHoraireMinutes = (value: string): number | null => {
  const match = value.match(/(\d+)/);
  if (!match) return null;
  return parseInt(match[1], 10);
};

const parsePrixAmount = (value: string): number | null => {
  const cleaned = value.replace(/[€\s]/g, "").replace(",", ".");
  if (cleaned === "") return null;
  const amount = parseFloat(cleaned);
  return Number.isNaN(amount) ? null : amount;
};

const toAnchors = (rows: PasteRow[]): PricingAnchor[] =>
  rows.reduce<PricingAnchor[]>((acc, row) => {
    const duration = parseHoraireMinutes(row.horaire);
    const priceAmount = parsePrixAmount(row.prix);
    if (duration === null || priceAmount === null) return acc;
    acc.push({ id: crypto.randomUUID(), duration, priceAmount });
    return acc;
  }, []);

type PricingPasteTableProps = {
  onApply?: (anchors: PricingAnchor[]) => void;
};

export const PricingPasteTable = ({ onApply }: PricingPasteTableProps) => {
  const [rows, setRows] = useState<PasteRow[]>([]);
  const [showParseWarning, setShowParseWarning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    const parsed = parsePastedTable(text);
    setShowParseWarning(parsed.length === 0);
    if (parsed.length > 0) setRows(parsed);
  };

  const updateCell = (index: number, field: keyof Omit<PasteRow, "id">, value: string) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, { id: crypto.randomUUID(), horaire: "", prix: "" }]);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const copyPrompt = async () => {
    await navigator.clipboard.writeText(AI_PROMPT);
    toast.success("Prompt copié dans le presse-papiers");
  };

  const reset = () => {
    setRows([]);
    setShowParseWarning(false);
  };

  const anchorsPreview = toAnchors(rows);

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        <div
          role="textbox"
          aria-label="Coller un tableau de tarification"
          tabIndex={0}
          onPaste={handlePaste}
          className="flex cursor-text flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-muted-foreground/25 p-10 text-center transition-colors hover:border-primary/40 hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <PasteIcon className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">Collez votre tableau ici</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            Cliquez puis <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">Ctrl+V</kbd> après
            avoir copié un tableau Horaire / Prix depuis un PDF ou Excel.
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              void copyPrompt();
            }}
            className="mt-1 gap-1.5 text-xs"
          >
            <CopyIcon className="h-3.5 w-3.5" />
            Copier le prompt pour l&apos;IA
          </Button>
        </div>
        {showParseWarning && (
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
            <WarningIcon className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Impossible de détecter des lignes dans le contenu collé. Vérifiez le format et réessayez.</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {rows.length} ligne{rows.length > 1 ? "s" : ""} importée{rows.length > 1 ? "s" : ""}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={reset} className="gap-1.5 text-xs">
          <RotateIcon className="h-3.5 w-3.5" />
          Recommencer
        </Button>
      </div>
      <div className="rounded-md border overflow-hidden">
        <Table className="table-fixed w-full">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="px-3">Horaire</TableHead>
              <TableHead className="px-3">Prix (€)</TableHead>
              <TableHead className="w-10 px-2"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell className="px-3 py-2">
                  <Input
                    value={row.horaire}
                    onChange={(e) => updateCell(index, "horaire", e.target.value)}
                    className="h-8 w-full"
                    placeholder="15 min"
                  />
                </TableCell>
                <TableCell className="px-3 py-2">
                  <Input
                    value={row.prix}
                    onChange={(e) => updateCell(index, "prix", e.target.value)}
                    className="h-8 w-full"
                    placeholder="5,00"
                  />
                </TableCell>
                <TableCell className="px-2 py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(index)}
                    className="mx-auto flex h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    title="Supprimer"
                  >
                    <DeleteIcon className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="outline" size="sm" onClick={addRow} className="w-full gap-1.5 text-xs sm:w-auto">
          <PlusIcon className="h-3.5 w-3.5" />
          Ajouter une ligne
        </Button>
        {onApply && (
          <Button
            type="button"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={anchorsPreview.length === 0}
            className="w-full gap-1.5 text-xs sm:w-auto"
          >
            <AnchorIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Appliquer comme ancrage</span>
          </Button>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remplacer les points d'ancrage</DialogTitle>
            <DialogDescription>
              Les {anchorsPreview.length} ligne{anchorsPreview.length > 1 ? "s" : ""} de ce tableau vont remplacer les
              points d&apos;ancrage actuels (onglet Saisie manuelle). Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                onApply?.(anchorsPreview);
                setConfirmOpen(false);
              }}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
